/**
 * BaseLayout - Layout base unificado para todos os roles
 *
 * Substitui a duplicação entre:
 * - AdminLayout (64 linhas)
 * - PartnerLayout (51 linhas)
 * - AssessorLayout (62 linhas)
 * - InvestorLayout (52 linhas)
 *
 * @example
 * // Uso direto
 * <BaseLayout
 *   sidebar={<AdminSidebar onSidebarToggle={setSidebarOpen} />}
 *   showHeader
 *   headerTitle="Dashboard Administrativo"
 *   showNotifications
 * >
 *   {children}
 * </BaseLayout>
 *
 * @example
 * // Via config
 * <BaseLayout config={adminLayoutConfig}>
 *   {children}
 * </BaseLayout>
 */

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { SkipLink } from '@/components/ui/SkipLink';
import { NotificationDropdown } from './NotificationDropdown';
import { MobileHeader } from './MobileHeader';
import { BottomNav, type BottomNavRole } from './BottomNav';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useScreenSize } from '@/hooks/use-mobile';
import { LumaSpin } from '@/components/ui/luma-spin';
import { cn } from '@/lib/utils';

// =============================================================================
// VARIANTS
// =============================================================================

const mainContentVariants = cva(
  'flex-1 overflow-auto max-w-full', // base
  {
    variants: {
      padding: {
        admin: '', // handled dynamically
        partner: '', // handled dynamically
        default: '', // handled dynamically
      },
      background: {
        gray: 'bg-gray-50',
        transparent: '',
      },
    },
    defaultVariants: {
      padding: 'default',
      background: 'transparent',
    },
  }
);

const headerVariants = cva(
  'flex items-center px-6 top-0 z-40', // base
  {
    variants: {
      variant: {
        full: 'h-14 border-b bg-white/80 backdrop-blur-sm sticky',
        minimal: 'h-4',
        hidden: 'hidden',
      },
    },
    defaultVariants: {
      variant: 'minimal',
    },
  }
);

// =============================================================================
// TYPES
// =============================================================================

export type LayoutRole = 'admin' | 'partner' | 'assessor' | 'investor' | 'incorporador';

export interface BaseLayoutConfig {
  /** Header variant */
  headerVariant?: 'full' | 'minimal' | 'hidden';
  /** Título do header (apenas para headerVariant='full') */
  headerTitle?: string;
  /** Mostrar dropdown de notificações no header */
  showNotifications?: boolean;
  /** Background do main content */
  background?: 'gray' | 'transparent';
  /** Padding customizado (override) */
  paddingClassName?: string;
  /** Role para o BottomNav mobile */
  bottomNavRole?: BottomNavRole;
}

export interface BaseLayoutProps extends VariantProps<typeof mainContentVariants> {
  /** Componente de sidebar a ser renderizado */
  sidebar: React.ReactNode;
  /** Conteúdo da página */
  children: React.ReactNode;
  /** Configuração do layout */
  config?: BaseLayoutConfig;
  /** Classes adicionais para o container */
  className?: string;
}

// =============================================================================
// CONFIGS PRE-DEFINIDAS
// =============================================================================

// =============================================================================
// COMPONENT
// =============================================================================

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  sidebar,
  children,
  config = {},
  className,
}) => {
  const { user, loading } = useAuth();
  const { isMobile, isTablet } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Merge config with defaults
  const {
    headerVariant = 'minimal',
    headerTitle,
    showNotifications = false,
    background = 'transparent',
    paddingClassName,
    bottomNavRole,
  } = config;

  // Loading state
  if (loading) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center',
        background === 'gray' && 'bg-gray-100'
      )}>
        <LumaSpin size="lg" />
      </div>
    );
  }

  // Auth check
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Calculate padding based on screen size
  const getPaddingClass = () => {
    if (paddingClassName) return paddingClassName;
    // Mobile: adiciona padding bottom para o BottomNav
    if (isMobile) return 'p-4 pt-2 pb-24';
    if (isTablet) return 'p-6';
    return 'px-8 pt-6 pb-4';
  };

  // Calculate margin based on sidebar state
  const getMarginClass = () => {
    if (isMobile) return '';
    return sidebarOpen ? 'ml-[300px]' : 'ml-[60px]';
  };

  // Clone sidebar with onSidebarToggle prop
  const sidebarWithProps = React.isValidElement(sidebar)
    ? React.cloneElement(sidebar as React.ReactElement<{ onSidebarToggle?: (open: boolean) => void }>, {
        onSidebarToggle: setSidebarOpen,
      })
    : sidebar;

  return (
    <div className={cn(
      'min-h-screen flex flex-col md:flex-row w-full max-w-full overflow-x-hidden overflow-y-auto',
      className
    )}>
      <SkipLink targetId="main-content" />

      {/* Sidebar (Desktop) */}
      {sidebarWithProps}

      {/* Main Content Wrapper */}
      <div className={cn(
        'flex-1 flex flex-col max-w-full ease-in-out',
        getMarginClass()
      )}>
        {/* Mobile Header */}
        {isMobile && (
          <MobileHeader showNotifications={showNotifications} />
        )}

        {/* Desktop Header */}
        {!isMobile && headerVariant !== 'hidden' && (
          <header className={headerVariants({ variant: headerVariant })}>
            {headerVariant === 'full' && (
              <>
                <div className="flex items-center space-x-2 flex-1">
                  {headerTitle && (
                    <h1 className="font-semibold text-foreground text-base">
                      {headerTitle}
                    </h1>
                  )}
                </div>
                {showNotifications && (
                  <div className="flex items-center space-x-2">
                    <NotificationDropdown />
                  </div>
                )}
              </>
            )}
          </header>
        )}

        {/* Desktop: bell flutuante para variants sem header full (minimal/hidden) */}
        {!isMobile && showNotifications && headerVariant !== 'full' && (
          <div className="fixed top-3 right-4 z-50">
            <div className="rounded-full bg-background/90 backdrop-blur-sm shadow-sm border border-border">
              <NotificationDropdown />
            </div>
          </div>
        )}

        {/* Page Content */}
        <main
          id="main-content"
          role="main"
          aria-label="Conteúdo principal"
          className={cn(
            mainContentVariants({ background }),
            getPaddingClass()
          )}
        >
          <div className="max-w-full overflow-hidden">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && bottomNavRole && (
        <BottomNav role={bottomNavRole} />
      )}
    </div>
  );
};

export default BaseLayout;
